import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useUser } from '../../contexts/UserContext';
import ConsultantForm from '../admin/consultants/ConsultantForm';
import { getCurrentConsultant, updateConsultant } from './api';

const normalizeApiErrors = (error) => {
  const rawErrors = error?.response?.data?.errors;
  if (!rawErrors) return { base: 'Could not save. Please try again.' };

  if (Array.isArray(rawErrors)) {
    return { base: rawErrors.join(', ') };
  }

  if (typeof rawErrors === 'object') {
    return Object.entries(rawErrors).reduce((acc, [key, value]) => {
      acc[key] = Array.isArray(value) ? value.join(', ') : String(value);
      return acc;
    }, {});
  }

  return { base: 'Could not save. Please try again.' };
};

const getConsultantId = (consultant, user) =>
  consultant?.id ||
  user?.consultant_id ||
  user?.consultant?.id ||
  null;

const ConsultantSettingsPage = () => {
  const toast = useToast();
  const { user } = useUser();
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const [consultant, setConsultant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [apiErrors, setApiErrors] = useState({});

  const consultantId = useMemo(() => getConsultantId(consultant, user), [consultant, user]);

  const loadConsultant = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const consultantRecord = await getCurrentConsultant();
      if (!consultantRecord && !user?.consultant_id && !user?.consultant?.id) {
        setError('Could not load consultant settings.');
        return;
      }
      setConsultant(consultantRecord || user?.consultant || null);
    } catch (err) {
      console.error('Error loading consultant settings:', err);
      setError('Could not load consultant settings.');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadConsultant();
  }, [loadConsultant]);

  const handleSubmit = async (payload) => {
    const id = getConsultantId(consultant, user);
    if (!id) {
      setApiErrors({ base: 'Could not identify consultant.' });
      return;
    }

    setIsSubmitting(true);
    setApiErrors({});

    const consultantPayload = {
      name: payload?.name?.trim() || '',
      email: payload?.email?.trim() || '',
      slug: payload?.slug?.trim() || '',
      booking_url: payload?.booking_url?.trim() || '',
      active: Boolean(payload?.active),
    };

    try {
      await updateConsultant(id, consultantPayload);
      toast({
        title: 'Consultant settings saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await loadConsultant();
    } catch (err) {
      console.error('Error saving consultant settings:', err);
      setApiErrors(normalizeApiErrors(err));
      toast({
        title: 'Could not save consultant settings',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <HStack justify="center" py={12}><Spinner size="lg" /></HStack>;
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Heading size="lg">Consultant Settings</Heading>
        <Text color={mutedColor} mt={1}>
          View and edit consultant details.
        </Text>
      </Box>

      <ConsultantForm
        title={consultant?.name || 'Consultant details'}
        initialData={{
          ...consultant,
          id: consultantId,
        }}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel="Save changes"
        apiErrors={apiErrors}
        hideCancel
      />
    </VStack>
  );
};

export default ConsultantSettingsPage;
